import React, { useEffect, useState } from "react";
import "react-toastify/dist/ReactToastify.css";
import "./SignApp.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { createPortal } from "react-dom";
import { ToastContainer, Bounce } from "react-toastify";
import boxedLogo from "../../../assets/images/theme/logo/logo-light.png";
import boxedLogoLight from "../../../assets/images/theme/logo/logo-dark.png";
import SignUp from "./components/SignUp";
import SignIn from "./components/SignIn";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import Preloader from "./components/Preloader";
import useSessionStore from "./stores/useSessionStore";

const FlashlightEffect = () => {
  const location = useLocation();

  useEffect(() => {
    const cards = document.querySelectorAll(".bg-flashlight");
    cards.forEach((bgflashlight) => {
      bgflashlight.onmousemove = function (e) {
        let x = e.pageX - bgflashlight.offsetLeft;
        let y = e.pageY - bgflashlight.offsetTop;

        bgflashlight.style.setProperty("--x", x + "px");
        bgflashlight.style.setProperty("--y", y + "px");
      };
    });
  }, [location.pathname]);

  return null;
};

const SignApp = () => {
  const [isLightTheme, setLightTheme] = useState(true);
  const isLoggedIn = useSessionStore((state) => state.isLoggedIn);
  const checkingSession = useSessionStore((state) => state.checkingSession);
  const checkSession = useSessionStore((state) => state.checkSession);

  useEffect(() => {
    checkSession();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      const user = useSessionStore.getState().user;
      const homePath =
        user?.user_type === "publisher"
          ? "/publishers/dashboard"
          : "/advertisers/dashboard";
      // Check for stored redirect path from expired session
      const redirectPath = localStorage.getItem("redirectAfterLogin");
      if (
        redirectPath &&
        ["/employers/", "/advertisers/", "/publishers/"].some((prefix) =>
          redirectPath.startsWith(prefix)
        )
      ) {
        localStorage.removeItem("redirectAfterLogin");
        window.location.href = redirectPath;
      } else {
        window.location.href = homePath;
      }
    }
  }, [isLoggedIn]);

  useEffect(() => {
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function (key, value) {
      originalSetItem.apply(this, arguments);
      if (key === "chatenai-theme") {
        window.dispatchEvent(new Event("chatenai-theme-change"));
      }
    };

    const handleThemeChange = () => {
      const themeType = localStorage.getItem("chatenai-theme");
      setLightTheme(themeType !== "dark");
    };

    window.addEventListener("storage", handleThemeChange);
    window.addEventListener("chatenai-theme-change", handleThemeChange);

    // Initial load
    handleThemeChange();

    return () => {
      window.removeEventListener("storage", handleThemeChange);
      window.removeEventListener("chatenai-theme-change", handleThemeChange);
      localStorage.setItem = originalSetItem;
    };
  }, []);

  if (checkingSession || isLoggedIn) return <Preloader />;

  return (
    <BrowserRouter>
      <FlashlightEffect />
      {createPortal(
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={false}
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme={isLightTheme ? "dark" : "light"}
          transition={Bounce}
        />,
        document.body
      )}
      <Routes>
        <Route
          path="/employers/sign-in"
          element={
            <SignIn
              boxedLogo={boxedLogo}
              boxedLogoLight={boxedLogoLight}
              isLightTheme={isLightTheme}
              signUpPath="/advertisers/sign-up"
              forgotPasswordPath="/advertisers/forgot-password"
              title="Advertiser Sign-In"
            />
          }
        />
        <Route
          path="/employers/sign-up"
          element={
            <SignUp
              boxedLogo={boxedLogo}
              boxedLogoLight={boxedLogoLight}
              isLightTheme={isLightTheme}
              accountType="employer"
              signInPath="/advertisers/sign-in"
              title="Advertiser Sign-Up"
            />
          }
        />
        <Route
          path="/advertisers/sign-in"
          element={
            <SignIn
              boxedLogo={boxedLogo}
              boxedLogoLight={boxedLogoLight}
              isLightTheme={isLightTheme}
              signUpPath="/advertisers/sign-up"
              forgotPasswordPath="/advertisers/forgot-password"
              title="Advertiser Sign-In"
            />
          }
        />
        <Route
          path="/advertisers/sign-up"
          element={
            <SignUp
              boxedLogo={boxedLogo}
              boxedLogoLight={boxedLogoLight}
              isLightTheme={isLightTheme}
              accountType="employer"
              signInPath="/advertisers/sign-in"
              title="Advertiser Sign-Up"
            />
          }
        />
        <Route
          path="/publishers/sign-in"
          element={
            <SignIn
              boxedLogo={boxedLogo}
              boxedLogoLight={boxedLogoLight}
              isLightTheme={isLightTheme}
              signUpPath="/publishers/sign-up"
              forgotPasswordPath="/publishers/forgot-password"
              title="Publisher Sign-In"
            />
          }
        />
        <Route
          path="/publishers/sign-up"
          element={
            <SignUp
              boxedLogo={boxedLogo}
              boxedLogoLight={boxedLogoLight}
              isLightTheme={isLightTheme}
              accountType="publisher"
              signInPath="/publishers/sign-in"
              title="Publisher Sign-Up"
            />
          }
        />
        <Route
          path="/employers/forgot-password"
          element={
            <ForgotPassword
              boxedLogo={boxedLogo}
              boxedLogoLight={boxedLogoLight}
              isLightTheme={isLightTheme}
            />
          }
        />
        <Route
          path="/advertisers/forgot-password"
          element={
            <ForgotPassword
              boxedLogo={boxedLogo}
              boxedLogoLight={boxedLogoLight}
              isLightTheme={isLightTheme}
            />
          }
        />
        <Route
          path="/publishers/forgot-password"
          element={
            <ForgotPassword
              boxedLogo={boxedLogo}
              boxedLogoLight={boxedLogoLight}
              isLightTheme={isLightTheme}
            />
          }
        />
        <Route
          path="/employers/reset-password"
          element={
            <ResetPassword
              boxedLogo={boxedLogo}
              boxedLogoLight={boxedLogoLight}
              isLightTheme={isLightTheme}
            />
          }
        />
        <Route
          path="/advertisers/reset-password"
          element={
            <ResetPassword
              boxedLogo={boxedLogo}
              boxedLogoLight={boxedLogoLight}
              isLightTheme={isLightTheme}
            />
          }
        />
        <Route
          path="/publishers/reset-password"
          element={
            <ResetPassword
              boxedLogo={boxedLogo}
              boxedLogoLight={boxedLogoLight}
              isLightTheme={isLightTheme}
            />
          }
        />
        <Route
          path="*"
          element={<Navigate to="/advertisers/sign-in" replace />}
        />
      </Routes>
    </BrowserRouter>
  );
};

export default SignApp;
